// AppShell
// --------
// The authenticated portal shell: the Session + Requests context providers and
// the persistent chrome (header nav, the security-settings panel, the session-
// expiry warning banner) that wrap every top-level screen and request sub-view.
// Extracted from portal.jsx's per-render `wrap()` helper (Phase 5). App owns the
// state and passes the provider values + the chrome handlers; AppShell renders
// the frame and its `children` (the active screen / sub-view). It reads the
// active path itself via useLocation (it renders inside <BrowserRouter>).

import { useLocation } from "react-router-dom";
import { ActiveBanners } from "../components/admin/ActiveBanners.jsx";
import { Header } from "../components/dashboard/DashboardPages.jsx";
import { SecurityPanel } from "../components/dashboard/ReportsPage.jsx";
import { Breadcrumb } from "./Breadcrumb.jsx";
import { PushFailureBanner } from "./PushFailureBanner.jsx";
import { RequestsProvider } from "./RequestsContext.jsx";
import { SessionProvider } from "./SessionContext.jsx";
import { SyncFailureBanner } from "./SyncFailureBanner.jsx";
import { UiConfigProvider } from "./UiConfigContext.jsx";

export function AppShell({
  sessionValue,
  requestsValue,
  onNavigate,
  onShowSecurity,
  showSecurity,
  onCloseSecurity,
  sessionWarning,
  // Real minutes remaining when the banner appears — NOT always 5. The configured
  // logout period is admin-editable down to 1 minute, and the warning is clamped to
  // stay strictly before it, so a short period gives a shorter lead. Finding B16 item 6.
  sessionWarnMinutes = 5,
  onDismissWarning,
  children,
}) {
  const location = useLocation();
  return (
    <SessionProvider value={sessionValue}>
      <RequestsProvider value={requestsValue}>
        <div
          style={{
            fontFamily: "system-ui,-apple-system,BlinkMacSystemFont,Segoe UI,sans-serif",
            minHeight: "100vh",
            background: "#F7F9F9",
          }}
        >
          {/* page = first path segment only, so a sub-path like
              /users/:shelterCode still highlights its parent nav item. */}
          <Header
            page={location.pathname.replace(/^\//, "").split("/")[0]}
            setPage={onNavigate}
            onShowSecurity={onShowSecurity}
          />
          {/* Site-wide announcement strip — mounted ONCE here (live-lists Batch B) so it
              shows on every authenticated screen, not just the three dashboards. Reads the
              viewer from useSession itself. */}
          <ActiveBanners />
          <Breadcrumb
            page={location.pathname.replace(/^\//, "").split("/")[0]}
            onNavigate={onNavigate}
          />
          {showSecurity && <SecurityPanel onClose={onCloseSecurity} />}
          <PushFailureBanner pathKey={location.pathname} onNavigate={onNavigate} />
          <SyncFailureBanner pathKey={location.pathname} onNavigate={onNavigate} />
          {sessionWarning && (
            <div
              style={{
                background: "#F59E0B",
                color: "white",
                textAlign: "center",
                padding: "16px 20px",
                fontSize: 15,
                fontFamily: "system-ui,-apple-system,BlinkMacSystemFont,Segoe UI,sans-serif",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: 20,
                boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
              }}
            >
              <span style={{ fontWeight: 700 }}>
                ⚠️ Your session will expire in {sessionWarnMinutes}{" "}
                {sessionWarnMinutes === 1 ? "minute" : "minutes"}. Any draft in progress has been
                saved.
              </span>
              <button
                onClick={onDismissWarning}
                style={{
                  background: "white",
                  color: "#92400E",
                  border: "none",
                  borderRadius: 6,
                  padding: "8px 20px",
                  fontSize: 14,
                  cursor: "pointer",
                  fontWeight: 700,
                  fontFamily: "inherit",
                  boxShadow: "0 1px 4px rgba(0,0,0,0.15)",
                }}
              >
                Keep me logged in
              </button>
            </div>
          )}
          <UiConfigProvider>{children}</UiConfigProvider>
        </div>
      </RequestsProvider>
    </SessionProvider>
  );
}
